import React from 'react'
import cx from 'classnames'
// import { Debug } from 'components/debug'
import moment from 'moment'
import { useSelector } from 'react-redux'
import Icon from 'components/common/Icon'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import HelpTip from 'components/common/HelpTip'
import { Textarea } from 'components/common/FormFields/Textarea/Textarea'

// The widgets below are chosen at runtime by `Widget` from a form schema, so
// their props are not statically known.
type WidgetProps = Record<string, any>

const ErrorMessage = ({
  id,
  children,
}: {
  id?: string
  children?: React.ReactNode
}) => (
  <div id={id} className="ErrorMessage">
    <Icon name="alert" /> {children}
  </div>
)

const ModelSelect = ({ target, ...props }: WidgetProps) => {
  target.useModel()
  const options = useSelector(target.getOptions) || []
  return <SelectWidget {...props} options={options} />
}

const eventHandlerFactory = (callBack, props) => data => {
  const newValue = props.isMulti
    ? Array.isArray(data)
      ? data.map(option => option.value)
      : []
    : data?.value
  const fakeEvent = { target: { value: newValue || props.defaultValue } }
  callBack(fakeEvent)
}

const SelectWidget = ({
  onBlur,
  onChange,
  value,
  options,
  creatable = false,
  ...props
}: WidgetProps) => {
  const findValue = v => options.find(option => option?.value === v)
  const selected = props.isMulti
    ? value.map(v => findValue(v))
    : findValue(value)

  const selectProps = {
    ...props,
    'aria-label': props.name,
    options,
    value: selected != null ? selected : null,
    onBlur: onBlur && eventHandlerFactory(onBlur, props),
    onChange: onChange && eventHandlerFactory(onChange, props),
    classNamePrefix: 'react-select',
  }
  return creatable ? (
    <CreatableSelect {...selectProps} />
  ) : (
    <Select {...selectProps} />
  )
}

const Checkbox = ({ value, onChange, ...props }: WidgetProps) => (
  // use checked boolean as value for onChange handler to work properly with
  // ModelFormField
  <input
    type="checkbox"
    checked={value}
    onChange={ev => onChange({ target: { value: ev.target.checked } })}
    {...props}
  />
)

const DateField = ({ value, ...props }: WidgetProps) => (
  <input
    type="date"
    value={value && moment(value).format('YYYY-MM-DD')}
    {...props}
  />
)

const DateTimeField = ({ value, ...props }: WidgetProps) => (
  <input
    type="datetime-local"
    value={value && moment(value).format('YYYY-MM-DDTHH:mm')}
    {...props}
  />
)

const Widget = ({ type, ...props }: WidgetProps) => {
  if (type === 'modelselect') return <ModelSelect {...props} />
  if (type === 'select') return <SelectWidget {...props} />
  if (type === 'textarea') return <Textarea {...props} />
  if (type === 'checkbox') return <Checkbox {...props} />
  if (type === 'date') return <DateField {...props} />
  if (type === 'datetime') return <DateTimeField {...props} />
  return <input type={type} {...props} />
}

type FormFieldProps = {
  className?: string
  name?: string
  label?: React.ReactNode
  placeholder?: React.ReactNode
  helptext?: string
  type?: string
  errors?: string[]
  onChange?: (...args: any[]) => void
  onBlur?: (...args: any[]) => void
  focus?: boolean
  hover?: boolean
  style?: React.CSSProperties
  [key: string]: any
}

export const FormField = ({
  className,
  name,
  label,
  placeholder = label,
  helptext,
  type = 'text',
  errors = [],
  onChange,
  onBlur,
  focus = false,
  hover = false,
  style,
  ...props
}: FormFieldProps) => {
  const error = errors.length !== 0
  // ids that link the messages to the input, so a screen reader announces them
  const errorIds = name ? errors.map((_, idx) => `${name}-error-${idx}`) : []
  const [focused, setFocus] = React.useState(focus)
  return (
    <label htmlFor={name} style={style} className={cx('FormField', className)}>
      {label && <span className="label">{label}</span>}
      {helptext && (
        <HelpTip>
          {helptext.split(/\n/g).map((txt, idx) => (
            <p key={idx}>{txt}</p>
          ))}
        </HelpTip>
      )}
      <Widget
        id={name}
        name={name}
        className={cx({ focus, hover, error })}
        noValidate
        type={type}
        onChange={onChange}
        onFocus={ev => setFocus(true)}
        onBlur={ev => {
          setFocus(false)
          onBlur && onBlur(ev)
        }}
        placeholder={focused ? placeholder : label || placeholder}
        aria-invalid={error || undefined}
        aria-describedby={errorIds.join(' ') || undefined}
        {...props}
      />
      {errors.map((msg, idx) => (
        <ErrorMessage key={msg} id={errorIds[idx]} children={msg} />
      ))}
    </label>
  )
}

export default FormField
